import type { SocialOpportunitiesWidget } from '@inference.engine/widgets/web';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { useTranslation } from 'react-i18next';

const MAX_PREVIEW_ITEMS = 4;

interface Props {
  data: SocialOpportunitiesWidget;
}

/**
 * Full-bleed hero resolved live from the lead opportunity's generated thumbnail
 * asset. Keeps a neutral placeholder background while the download-url query is
 * in flight so the card never flashes a broken image.
 */
function OpportunityHero(props: { assetId: string; title: string }) {
  const { downloadUrl } = useGetDownloadUrl(props.assetId);
  return (
    <div className="h-full w-full bg-[#EFEFF0]">
      {downloadUrl && (
        <img src={downloadUrl} alt={props.title} className="h-full w-full object-cover" />
      )}
    </div>
  );
}

/**
 * Prop-only preview of a social-opportunities widget. When the lead opportunity
 * carries a generated thumbnail it renders as a full-bleed hero photo (echoing
 * the opportunity card); otherwise it falls back to the brand name plus a few
 * opportunity titles. Reads props.data only — the hero fetch is a read-only
 * download-url query (cached per assetId), never the widget store.
 */
export const SocialMediaOpportunitiesThumbnail = (props: Props) => {
  const { t } = useTranslation('analysis');

  const items = Array.isArray(props.data.items) ? props.data.items : [];
  const lead = items.length > 0 ? items[0] : undefined;

  if (lead?.thumbnail?.assetId) {
    return <OpportunityHero assetId={lead.thumbnail.assetId} title={lead.opportunityTitle} />;
  }

  const previewItems = items.slice(0, MAX_PREVIEW_ITEMS);
  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      <p className="truncate text-sm font-semibold text-[#1A1A1A]">
        {props.data.brandName ||
          t('socialMediaOpportunitiesWidget.socialMediaOpportunitiesThumbnail.opportunities')}
      </p>
      <ul className="flex flex-col gap-1">
        {previewItems.map((item) => (
          <li key={item.opportunityId} className="line-clamp-1 text-xs text-[#1A1A1A]">
            {item.opportunityTitle}
          </li>
        ))}
      </ul>
    </div>
  );
};
